/**
 * Camada das estações do CEMADEN no mapa.
 *
 * Camada vetorial comum do OpenLayers: são ~570 pontos, três ordens de
 * grandeza abaixo dos focos, então o renderizador próprio em WebGL que
 * `fires/layer.ts` precisa aqui não se justifica — e o estilo nativo do OL já
 * entra na composição da exportação.
 *
 * O símbolo é uma GOTA (ver `drop.ts`), desenhada num sprite de canvas e
 * reaproveitada por `ol/style/Icon`. Um ponto circular se confundiria com as
 * sedes municipais, que já usam essa forma.
 *
 * A cada quadro do loop o estilo é reavaliado a partir de `series.ts`: é isso
 * que faz a chuva avançar junto com a animação.
 */
import type { ReactNode } from 'react';
import Feature from 'ol/Feature';
import Point from 'ol/geom/Point';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import CircleStyle from 'ol/style/Circle';
import Icon from 'ol/style/Icon';
import Stroke from 'ol/style/Stroke';
import Style from 'ol/style/Style';
import type { FeatureLike } from 'ol/Feature';
import { comSelo } from '../map/contraste';
import { criarCache, dpr, fatorDoZoom } from '../map/escalaDoZoom';
import { Z } from '../map/zIndex';
import { useTimeStore } from '../state/time';
import { CEMADEN_ATTRIBUTION } from './api';
import { GOTA_EXTENT, gotaPath } from './drop';
import { haloDe, visualDe, type Halo, type Visual } from './look';
import { chuvaNaFatia, slotDe, totalDoPeriodo, type Leitura } from './series';
import { useStationStore } from './store';
import type { Estacao, Estilo } from './types';

export interface EstacaoFeature {
  estacao: Estacao;
  /** Chuva no período inteiro — estável entre quadros, é o que colore a gota. */
  total: Leitura;
  /** Chuva na fatia do quadro exibido em mm; `null` = desconhecido. Acende o halo. */
  agora: number | null;
}

const SEM_LEITURA: Leitura = { mm: null, medidas: 0, fatias: 0 };

// ---------------------------------------------------------------------------
// Sprites da gota
// ---------------------------------------------------------------------------

/**
 * Um sprite por aparência distinta. Com 569 estações e poucas cores em uso, o
 * cache costuma ficar em algumas dezenas de entradas — desenhar a gota uma vez
 * por estação e por quadro seria desperdício.
 */
const sprites = criarCache<HTMLCanvasElement>('pluviometros.sprites', 800);

/**
 * Estilos prontos, na MESMA chave do sprite.
 *
 * A gota depende só do total do período, que não muda entre quadros: sem este
 * cache, um `layer.changed()` a 5 fps reconstruía ~570 objetos `Style`+`Icon`
 * por quadro para desenhar exatamente o mesmo pixel.
 */
const estilosGota = criarCache<Style>('pluviometros.gotas', 800);

/**
 * Halos prontos. Antes cada chamada criava um `Style` novo; com o zoom
 * reavaliando os ~570 pontos a cada mudança de nível, isso virou custo.
 */
const estilosHalo = criarCache<Style>('pluviometros.halos', 600);

/**
 * O halo para de crescer no fator 1,0: é o maior desenho da camada (1,9× o
 * tamanho na chuva forte, ~29 px na base de 7) e, acompanhando a gota até 1,3,
 * passaria de 37 px.
 */
export const FATOR_MAX_DO_HALO = 1;

/** Degrau do raio em px: o raio cresce com a chuva de forma contínua, e com o fator do zoom por cima o cache não teria fim. */
const DEGRAU_RAIO = 0.25;

function emDegraus(v: number, degrau: number): number {
  return Math.round(v / degrau) * degrau;
}

function estiloHalo(h: Halo): Style {
  const raio = emDegraus(h.raio, 0.5);
  const largura = emDegraus(h.largura, 0.2);
  const k = `${raio}|${largura.toFixed(1)}|${h.cor}`;
  let s = estilosHalo.get(k);
  if (!s) {
    s = new Style({
      image: new CircleStyle({
        radius: raio,
        stroke: new Stroke({ color: h.cor, width: largura }),
      }),
      // Atrás da gota, para o anel não cobrir a cor do total.
      zIndex: -1,
    });
    estilosHalo.set(k, s);
  }
  return s;
}

/**
 * Chave da aparência. O DPR entra nela porque o sprite é desenhado em px do
 * dispositivo: arrastar a janela de um monitor 2× para um 1× reaproveitaria o
 * canvas antigo com a escala nova, dobrando o tamanho das gotas.
 */
function chave(v: Visual): string {
  return `${dpr()}|${v.raio.toFixed(2)}|${v.preenchimento ?? '-'}|${v.contorno}|${v.larguraContorno}|${v.tracejado.join(',')}|${v.opacidade.toFixed(3)}`;
}

/** Desenha a gota num canvas, com folga para o contorno. */
function sprite(v: Visual, k: string): HTMLCanvasElement | null {
  const pronto = sprites.get(k);
  if (pronto) return pronto;

  const escala = dpr();
  const folga = v.larguraContorno + 1;
  const meio = v.raio * GOTA_EXTENT + folga;
  const lado = Math.max(4, Math.ceil(meio * 2 * escala));

  const canvas = document.createElement('canvas');
  canvas.width = lado;
  canvas.height = lado;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;

  ctx.scale(escala, escala);
  ctx.translate(meio, meio);
  ctx.scale(v.raio, v.raio);

  const path = gotaPath();
  if (v.preenchimento) {
    ctx.fillStyle = v.preenchimento;
    ctx.fill(path);
  }
  ctx.strokeStyle = v.contorno;
  // A largura é em px de tela, mas o contexto está na escala do raio.
  ctx.lineWidth = v.larguraContorno / v.raio;
  if (v.tracejado.length) ctx.setLineDash(v.tracejado.map((d) => d / v.raio));
  ctx.lineJoin = 'round';
  ctx.stroke(path);

  sprites.set(k, canvas);
  return canvas;
}

/**
 * Aparência de uma estação na resolução do mapa (função pura): a gota pelo
 * fator do zoom, com o raio em degraus, e o halo pelo fator limitado.
 */
export function visualNoZoom(total: Leitura, agora: number | null, estilo: Estilo, resolution: number | undefined): Visual {
  const fator = fatorDoZoom(resolution);
  const visual = visualDe(total, agora, estilo.tamanho * fator, estilo.opacidade);
  return {
    ...visual,
    raio: Math.max(DEGRAU_RAIO, emDegraus(visual.raio, DEGRAU_RAIO)),
    halo: visual.halo && agora !== null ? haloDe(agora, estilo.tamanho * Math.min(fator, FATOR_MAX_DO_HALO), estilo.opacidade) : null,
  };
}

/**
 * Estilos de uma estação: o halo (quando chove nesta fatia) e a gota.
 * Vazio quando não há nada a desenhar.
 *
 * O raio da gota vai em degraus de 0,25 px e os estilos (gota e halo) ficam
 * em cache com teto.
 *
 * A gota depende só de `total`, que não muda entre quadros — então o sprite é
 * o mesmo e ela não pisca. O halo é a única parte temporal.
 *
 * Com `resolution` na faixa da foto de fundo, a gota ganha o selo escuro por
 * baixo (map/contraste.ts): a vazada de "sem chuva" sumia na foto.
 */
export function estiloDe(total: Leitura, agora: number | null, estilo: Estilo, resolution?: number): Style[] {
  const v = visualNoZoom(total, agora, estilo, resolution);
  // Estações sem dado só somem se a pessoa pedir; o padrão é mostrar tudo.
  if (v.estado === 'semDado' && !estilo.mostrarSemDado) return [];

  const k = chave(v);
  const img = sprite(v, k);
  if (!img) return [];

  let gota = estilosGota.get(k);
  if (!gota) {
    gota = new Style({
      image: new Icon({
        img,
        // O sprite é desenhado com a origem no centro do bulbo, que é o ponto
        // que corresponde à posição real da estação — a ponta sobra para cima.
        anchor: [0.5, 0.5],
        opacity: v.opacidade,
        // O sprite é desenhado em px do dispositivo; aqui volta para px de CSS.
        scale: 1 / dpr(),
      }),
    });
    estilosGota.set(k, gota);
  }

  // Selo em volta da gota inteira: o bulbo (raio 0,78) fica na âncora e a
  // ponta sobe até 1,82 — o meio do desenho está 0,52 acima, a meia-altura é 1,3.
  const selo = (estilos: Style[]) => comSelo(estilos, 1.3 * v.raio + v.larguraContorno + 2, resolution, 0.52 * v.raio);
  if (!v.halo) return selo([gota]);
  return selo([estiloHalo(v.halo), gota]);
}

export interface StationLayer {
  layer: VectorLayer<VectorSource>;
  dispose: () => void;
}

export function createStationLayer(renderPopup: (f: EstacaoFeature) => ReactNode | null): StationLayer {
  const source = new VectorSource({ attributions: CEMADEN_ATTRIBUTION });
  const layer = new VectorLayer({
    source,
    // Abaixo dos focos: queimada é o assunto principal do AmaView e não pode
    // ser encoberta pela chuva.
    zIndex: Z.STATIONS,
    className: 'ol-layer amaview-stations',
    properties: { id: 'stations', title: 'Estações do CEMADEN' },
    updateWhileAnimating: false,
    updateWhileInteracting: false,
  });

  layer.setStyle((feature, resolution) => {
    const d = feature.get('estacao') as EstacaoFeature | undefined;
    return d ? estiloDe(d.total, d.agora, useStationStore.getState().estilo, resolution) : [];
  });

  layer.set('popup', (feature: FeatureLike): ReactNode | null => {
    const d = feature.get('estacao') as EstacaoFeature | undefined;
    return d ? renderPopup(d) : null;
  });

  let codigosDesenhados: readonly string[] = [];

  /** Recria as features quando o conjunto de estações muda (raro). */
  function reconstruir(): void {
    const { dados } = useStationStore.getState();
    source.clear(true);
    source.addFeatures(
      dados.estacoes.map((e) => {
        const f = new Feature({ geometry: new Point([e.mx, e.my]) });
        f.set('estacao', { estacao: e, total: SEM_LEITURA, agora: null } satisfies EstacaoFeature);
        return f;
      }),
    );
    codigosDesenhados = dados.estacoes.map((e) => e.cod);
    recalcularTotais();
    reavaliarHalos();
    layer.changed();
  }

  /**
   * Recalcula o total do período de cada estação. Só depende dos dados, então
   * roda quando eles chegam — e não a cada quadro.
   */
  function recalcularTotais(): void {
    const { dados } = useStationStore.getState();
    const grade = dados.grade;
    for (const f of source.getFeatures()) {
      const d = f.get('estacao') as EstacaoFeature;
      const i = dados.linhaDe.get(d.estacao.cod);
      const total = grade && i !== undefined ? totalDoPeriodo(grade.v[i], grade.grandeza) : SEM_LEITURA;
      f.set('estacao', { estacao: d.estacao, total, agora: d.agora } satisfies EstacaoFeature, true);
    }
  }

  /**
   * Acende o halo nas estações que recebem chuva na fatia do quadro exibido.
   *
   * Só redesenha quando alguma estação de fato muda de estado, senão o loop
   * mandaria a camada se repintar 5 vezes por segundo sem nada novo.
   */
  function reavaliarHalos(): void {
    const { dados, visivel } = useStationStore.getState();
    layer.setVisible(visivel);
    if (!visivel) return;

    const grade = dados.grade;
    const { displayedTime, currentTime } = useTimeStore.getState();
    const instante = displayedTime ?? currentTime;
    const slot = grade && instante !== null ? slotDe(grade, instante) : -1;

    let mudou = false;
    for (const f of source.getFeatures()) {
      const d = f.get('estacao') as EstacaoFeature;
      const i = dados.linhaDe.get(d.estacao.cod);
      const agora = grade && slot >= 0 && i !== undefined ? chuvaNaFatia(grade.v[i], slot, grade.grandeza) : null;
      if (agora === d.agora) continue;
      f.set('estacao', { estacao: d.estacao, total: d.total, agora } satisfies EstacaoFeature, true);
      mudou = true;
    }
    if (mudou) layer.changed();
  }

  const pararStore = useStationStore.subscribe((a, b) => {
    if (a.dados !== b.dados) {
      const novos = a.dados.estacoes.map((e) => e.cod);
      const mudou = novos.length !== codigosDesenhados.length || novos.some((c, i) => c !== codigosDesenhados[i]);
      if (mudou) {
        reconstruir();
      } else {
        recalcularTotais();
        reavaliarHalos();
        layer.changed();
      }
      return;
    }
    // A janela de acumulação NÃO entra aqui: ela vale só para o popup. O mapa
    // mostra o total do período, que é estável.
    if (a.visivel !== b.visivel) reavaliarHalos();
    else if (a.estilo !== b.estilo) layer.changed();
  });

  // O quadro exibido mudou: a chuva anda junto.
  const pararTempo = useTimeStore.subscribe((a, b) => {
    if (a.displayedTime !== b.displayedTime || a.currentTime !== b.currentTime) reavaliarHalos();
  });

  reconstruir();

  return {
    layer,
    dispose: () => {
      pararStore();
      pararTempo();
      source.clear(true);
    },
  };
}
